/**
 * 配信 / Integration。「どこまで公開するか」と「外部への書き戻しの許可範囲」を
 * 1 枚で見て、反映の提案を承認・却下する。
 *
 * この面が守っていること:
 * - **押しても配信は始まらない。** 公開の切り替えは設定の保存だけで、
 *   配信を走らせる口は API に無い。そのことを区画ごとに書く
 * - **経路の無い対象（sitemap / llms.txt）は押せない形にし、理由を出す。**
 *   「公開していない」と読ませない
 * - **配信ログが空なのは「失敗が 0 件」ではなく「実行そのものがまだ無い」。**
 *   0 件と書かず、理由をそのまま出す
 * - **承認しても反映は走らない。** 承認の直後に「反映は、EC 側の口がつながって
 *   から行います」と出す
 * - 数えていない欄に「—」だけを置かない（理由を必ず添える）
 */

import { useMemo, useState } from "react";
import { Link } from "react-router";
import { useMutation, useQuery } from "urql";
import {
  ApproveWritebackProposalDocument,
  Button,
  Card,
  Disclosure,
  EmptyState,
  ErrorState,
  LoadingState,
  PageHeader,
  PublishOverviewDocument,
  RejectWritebackProposalDocument,
  SetPublishTargetDocument,
  SetWritebackPolicyDocument,
  Stat,
  Tag,
  WritebackPolicyDocument,
  WritebackProposalsDocument,
  formatJstDateTime,
  type PopulationId,
  type WritebackMode,
  type WritebackProposalState,
  type WritebackTarget,
} from "visumo-data-agent-web-shared";

import {
  DEFAULT_PUBLISH_POPULATION,
  DELIVERY_LOG_EMPTY_NOTE,
  NOT_MEASURED,
  PUBLISH_SCOPE_NOT_COUNTED_NOTE,
  PUBLISH_TOGGLE_IS_SETTING_ONLY_NOTE,
  SUPPORTS_PUBLISH_REFRESH,
  SUPPORTS_WRITEBACK_REVERT,
  WRITEBACK_APPROVE_DOES_NOT_APPLY_NOTE,
  WRITEBACK_AUTOMATIC_NOT_CONNECTED_NOTE,
  WRITEBACK_MODE_LABEL,
  WRITEBACK_NO_PREVIOUS_VALUE_NOTE,
  WRITEBACK_PROPOSALS_EMPTY_NOTE,
  WRITEBACK_PROPOSALS_FILTERED_EMPTY_NOTE,
  WRITEBACK_PROPOSAL_STATE_LABEL,
  WRITEBACK_TARGET_LABEL,
} from "../api-capabilities";
import { HelpIcon } from "../help/HelpIcon";
import { PopulationSelect } from "./PopulationSelect";

/** 許可範囲の選択肢。**弱い順**に並べる（既定は真ん中の PROPOSE_ONLY）。 */
const WRITEBACK_MODES: readonly WritebackMode[] = [
  "NEVER",
  "PROPOSE_ONLY",
  "AUTOMATIC",
];

/** 一覧の絞り込みに出す状態。いま実際に起こりうるものだけを並べる。 */
const FILTERABLE_STATES: readonly WritebackProposalState[] = [
  "PENDING",
  "APPROVED",
  "REJECTED",
];

/**
 * 一覧の既定は「承認待ち」。
 *
 * 終わった提案（承認済み・却下）は**片づける口が API に無く**、使うほど
 * 増え続ける。既定を「すべて」にすると、手を打てる提案が古い履歴に埋もれ、
 * 1 ページに載らなくなる。終わったものは切り替えで見る。
 */
const DEFAULT_PROPOSAL_STATE_FILTER: WritebackProposalState = "PENDING";

const STATE_TONE: Record<
  WritebackProposalState,
  "ok" | "warn" | "neutral" | "info" | "danger"
> = {
  PENDING: "warn",
  APPROVED: "info",
  APPLIED: "ok",
  REJECTED: "neutral",
  REVERTED: "neutral",
  FAILED: "danger",
};

function count(value: number | null | undefined): string {
  return value == null ? NOT_MEASURED : value.toLocaleString("ja-JP");
}

/** 1 操作につき 1 つ。再送のたびに作り直すと二重に書ける */
function newIdempotencyKey(): string {
  return crypto.randomUUID();
}

export function PublishPage() {
  // 1 画面 1 母数。ここを切り替えると「公開の範囲」の数字がその母数のものになる
  const [population, setPopulation] = useState<PopulationId>(
    DEFAULT_PUBLISH_POPULATION,
  );
  const [stateFilter, setStateFilter] = useState<WritebackProposalState | "">(
    DEFAULT_PROPOSAL_STATE_FILTER,
  );
  const [approved, setApproved] = useState<string | null>(null);

  const [overview, refetchOverview] = useQuery({
    query: PublishOverviewDocument,
    variables: { population },
  });
  const [policyResult, refetchPolicy] = useQuery({
    query: WritebackPolicyDocument,
  });
  /**
   * 提案は**別の画面**（商品ページのプレビュー）から起こされる。
   *
   * urql の document cache は「応答に入っていた型名」で無効化を決めるので、
   * この一覧が**空で返った回**の応答には `WritebackProposal` が 1 つも入らず、
   * あとから提案を起こしても無効にならない（配信を先に開いていると、
   * 戻ってきたときに古い空の一覧を見続ける）。空でも型名を依存に数えさせる。
   */
  const proposalsContext = useMemo(
    () => ({ additionalTypenames: ["WritebackProposal"] }),
    [],
  );
  const [proposalsResult, refetchProposals] = useQuery({
    query: WritebackProposalsDocument,
    variables: stateFilter === "" ? {} : { filter: { state: stateFilter } },
    context: proposalsContext,
  });

  const [targetResult, setPublishTarget] = useMutation(
    SetPublishTargetDocument,
  );
  const [policyWrite, setWritebackPolicy] = useMutation(
    SetWritebackPolicyDocument,
  );
  const [approveResult, approveProposal] = useMutation(
    ApproveWritebackProposalDocument,
  );
  const [rejectResult, rejectProposal] = useMutation(
    RejectWritebackProposalDocument,
  );

  if (overview.fetching && overview.data === undefined) {
    return <LoadingState />;
  }
  if (overview.error !== undefined || overview.data === undefined) {
    return (
      <ErrorState
        description={overview.error?.message}
        onRetry={() => refetchOverview({ requestPolicy: "network-only" })}
      />
    );
  }

  const targets = overview.data.publishTargets;
  const scope = overview.data.publishScope;
  const deliveries = overview.data.deliveryLog.edges;
  const policy = policyResult.data?.writebackPolicy ?? null;
  const proposals = proposalsResult.data?.writebackProposals.edges ?? [];

  const targetErrors = targetResult.data?.setPublishTarget.userErrors ?? [];
  const policyErrors = policyWrite.data?.setWritebackPolicy.userErrors ?? [];
  const actionErrors = [
    ...(approveResult.data?.approveWritebackProposal.userErrors ?? []),
    ...(rejectResult.data?.rejectWritebackProposal.userErrors ?? []),
  ];

  const toggleTarget = async (
    kind: (typeof targets)[number]["kind"],
    enabled: boolean,
  ): Promise<void> => {
    await setPublishTarget({
      input: { target: kind, enabled, idempotencyKey: newIdempotencyKey() },
    });
    refetchOverview({ requestPolicy: "network-only" });
  };

  /** 許可範囲は**全量**で送る。1 つ変えるときも、残りをそのまま添える。 */
  const changeMode = async (
    target: WritebackTarget,
    mode: WritebackMode,
  ): Promise<void> => {
    if (policy === null) return;
    const entries = [
      ...policy.entries
        .filter((entry) => !(entry.target === target && entry.field === null))
        .map((entry) => ({
          target: entry.target,
          field: entry.field,
          mode: entry.mode,
        })),
      { target, field: null, mode },
    ];
    await setWritebackPolicy({
      input: {
        entries,
        expectedVersion: policy.version,
        idempotencyKey: newIdempotencyKey(),
      },
    });
    refetchPolicy({ requestPolicy: "network-only" });
  };

  const decide = async (
    proposalId: string,
    decision: "APPROVE" | "REJECT",
  ): Promise<void> => {
    const input = { proposalId, idempotencyKey: newIdempotencyKey() };
    if (decision === "APPROVE") {
      const response = await approveProposal({ input });
      if (
        (response.data?.approveWritebackProposal.userErrors ?? []).length === 0
      ) {
        setApproved(proposalId);
      }
    } else {
      await rejectProposal({ input });
      setApproved(null);
    }
    refetchProposals({ requestPolicy: "network-only" });
  };

  /** その先にいま効いている強さ。**設定が無ければ既定**（fail-closed）。 */
  const modeFor = (target: WritebackTarget): WritebackMode => {
    const entry = (policy?.entries ?? []).find(
      (candidate) => candidate.target === target && candidate.field === null,
    );
    return entry?.mode ?? policy?.defaultMode ?? "PROPOSE_ONLY";
  };

  return (
    <div className="vd-page">
      <PageHeader
        title="配信"
        description="どこまで公開するかの設定と、外部への書き戻しの提案・承認をまとめています。"
        extra={
          <Stat
            label="最後に配信した時刻"
            value={NOT_MEASURED}
            note="配信を 1 度も始めていないため、配信した時刻がありません。0 件の配信という意味ではありません。"
          />
        }
      />

      <div className="vd-stack">
        {targetErrors.length + policyErrors.length + actionErrors.length ===
        0 ? null : (
          <div className="vd-state vd-state--error" role="alert">
            <span className="vd-state-title">設定を保存できませんでした</span>
            {[...targetErrors, ...policyErrors, ...actionErrors].map(
              (error) => (
                <span key={`${error.code}-${error.message}`}>
                  {error.message}
                </span>
              ),
            )}
          </div>
        )}

        <Card
          title="何を公開するか"
          note={PUBLISH_TOGGLE_IS_SETTING_ONLY_NOTE}
          extra={<HelpIcon slug="publish/targets" />}
        >
          <div className="vd-stack" data-testid="publish-targets">
            {targets.map((target) => (
              <div
                key={target.kind}
                data-testid={`publish-target-${target.kind}`}
                style={{
                  borderTop: "1px solid var(--vd-border)",
                  paddingTop: 12,
                }}
              >
                <div
                  className="vd-row"
                  style={{ justifyContent: "space-between" }}
                >
                  <div>
                    <div className="vd-card-title">
                      {target.label}
                      {target.available ? null : (
                        <Tag tone="neutral">経路がまだありません</Tag>
                      )}
                    </div>
                    <p
                      style={{
                        margin: "2px 0 0",
                        color: "var(--vd-text-secondary)",
                      }}
                    >
                      {target.detail}
                    </p>
                  </div>
                  <label className="vd-check">
                    <input
                      type="checkbox"
                      aria-label={`${target.label}の公開`}
                      checked={target.enabled}
                      disabled={!target.available || targetResult.fetching}
                      onChange={(event) => {
                        void toggleTarget(target.kind, event.target.checked);
                      }}
                    />
                    <span>{target.label}の公開</span>
                  </label>
                </div>
                {target.unavailableReason === null ? null : (
                  <p className="vd-stat-note" style={{ margin: "4px 0 0" }}>
                    {target.unavailableReason}
                  </p>
                )}
                <p className="vd-stat-note" style={{ margin: "4px 0 0" }}>
                  最後に公開した時刻: {NOT_MEASURED}（配信を始めていません）
                </p>
              </div>
            ))}
          </div>
          {SUPPORTS_PUBLISH_REFRESH ? null : (
            <p className="vd-stat-note" style={{ marginBottom: 0 }}>
              いま公開している内容を作り直す口は、まだありません（作り直す先の
              配信そのものが無いためです）。
            </p>
          )}
        </Card>

        <Card
          title="公開の範囲"
          note={`この区画の数字は「${scope.basis.label}」を母数にしています。${scope.basis.note}`}
        >
          <div className="vd-stack" data-testid="publish-scope">
            <PopulationSelect value={population} onChange={setPopulation} />
            <p style={{ margin: 0 }}>{scope.condition}</p>
            <div className="vd-row" style={{ gap: 24 }}>
              <Stat
                label="母数に入っている商品"
                value={count(scope.matched)}
                note={
                  scope.matched == null
                    ? "この母数では商品の件数を数えられません。"
                    : undefined
                }
              />
              <Stat
                label="組み立て済みのページ"
                value={NOT_MEASURED}
                note={PUBLISH_SCOPE_NOT_COUNTED_NOTE}
              />
              <Stat
                label="範囲から落ちているもの"
                value={NOT_MEASURED}
                note={scope.excludeReason}
              />
            </div>
            {scope.stoppedRules.length === 0 ? (
              <p className="vd-stat-note" style={{ margin: 0 }}>
                止めているルールはありません。
              </p>
            ) : (
              <p className="vd-stat-note" style={{ margin: 0 }}>
                止めているルールが {scope.stoppedRules.length} 本あります。
                そのルールが作った答えは商品ページに載りません（
                <Link to="/rules">ルール</Link>）。
              </p>
            )}
          </div>
        </Card>

        <Card title="配信ログ" note={DELIVERY_LOG_EMPTY_NOTE}>
          <div data-testid="delivery-log">
            {deliveries.length === 0 ? (
              <EmptyState
                title="送った記録がまだありません"
                description={DELIVERY_LOG_EMPTY_NOTE}
              />
            ) : (
              <div className="vd-table-scroll">
                <table className="vd-table">
                  <thead>
                    <tr>
                      <th>日時</th>
                      <th>対象</th>
                      <th>件数</th>
                      <th>結果</th>
                    </tr>
                  </thead>
                  <tbody>
                    {deliveries.map(({ node }) => (
                      <tr key={node.id}>
                        <td>{formatJstDateTime(node.at)}</td>
                        <td>{node.target}</td>
                        <td>{node.products.toLocaleString("ja-JP")}</td>
                        <td>{node.result}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </Card>

        <Card
          title="外部への書き戻しの許可範囲"
          note={`設定の無い先は「${WRITEBACK_MODE_LABEL[policy?.defaultMode ?? "PROPOSE_ONLY"]}」で埋まります。${WRITEBACK_AUTOMATIC_NOT_CONNECTED_NOTE}`}
        >
          {policyResult.fetching && policy === null ? (
            <LoadingState label="許可範囲を読み込んでいます" />
          ) : policy === null ? (
            <ErrorState
              title="許可範囲を読み込めませんでした"
              description={policyResult.error?.message}
              onRetry={() => refetchPolicy({ requestPolicy: "network-only" })}
            />
          ) : (
            <div className="vd-table-scroll" data-testid="writeback-policy">
              <table className="vd-table">
                <thead>
                  <tr>
                    <th>書き戻し先</th>
                    <th>許可の範囲</th>
                    <th>口の状態</th>
                  </tr>
                </thead>
                <tbody>
                  {policy.adapters.map((adapter) => (
                    <tr
                      key={adapter.target}
                      data-testid={`writeback-adapter-${adapter.target}`}
                    >
                      <td>{WRITEBACK_TARGET_LABEL[adapter.target]}</td>
                      <td>
                        <label className="vd-field">
                          <span className="vd-field-hint">
                            {WRITEBACK_TARGET_LABEL[adapter.target]}の許可範囲
                          </span>
                          <select
                            className="vd-select"
                            aria-label={`${WRITEBACK_TARGET_LABEL[adapter.target]}の許可範囲`}
                            value={modeFor(adapter.target)}
                            disabled={policyWrite.fetching}
                            onChange={(event) => {
                              void changeMode(
                                adapter.target,
                                event.target.value as WritebackMode,
                              );
                            }}
                          >
                            {WRITEBACK_MODES.map((mode) => (
                              <option key={mode} value={mode}>
                                {WRITEBACK_MODE_LABEL[mode]}
                              </option>
                            ))}
                          </select>
                        </label>
                      </td>
                      <td>
                        <Tag tone="neutral">まだ接続していません</Tag>
                        {adapter.note === null ? null : (
                          <p
                            className="vd-stat-note"
                            style={{ margin: "4px 0 0" }}
                          >
                            {adapter.note}
                          </p>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>

        <Card
          title="反映の提案"
          note={`承認しても反映は走りません。${WRITEBACK_NO_PREVIOUS_VALUE_NOTE}`}
          extra={
            <label className="vd-field">
              <span className="vd-field-hint">状態</span>
              <select
                className="vd-select"
                aria-label="提案の状態"
                value={stateFilter}
                onChange={(event) => {
                  setStateFilter(
                    event.target.value as WritebackProposalState | "",
                  );
                }}
              >
                <option value="">すべて</option>
                {FILTERABLE_STATES.map((state) => (
                  <option key={state} value={state}>
                    {WRITEBACK_PROPOSAL_STATE_LABEL[state]}
                  </option>
                ))}
              </select>
            </label>
          }
        >
          <div className="vd-stack" data-testid="writeback-proposals">
            {approved === null ? null : (
              <div className="vd-notice" role="status">
                {WRITEBACK_APPROVE_DOES_NOT_APPLY_NOTE}
              </div>
            )}
            {proposalsResult.fetching && proposals.length === 0 ? (
              <LoadingState label="提案を読み込んでいます" />
            ) : proposals.length === 0 ? (
              // **絞り込んだ結果の 0 件を「1 つも無い」と書かない。**
              // 終わった提案は残っているのに「まだありません」と出ると、
              // 履歴ごと消えたように読める
              stateFilter === "" ? (
                <EmptyState
                  title="反映の提案はまだありません"
                  description={WRITEBACK_PROPOSALS_EMPTY_NOTE}
                />
              ) : (
                <EmptyState
                  title={`${WRITEBACK_PROPOSAL_STATE_LABEL[stateFilter]}の提案はありません`}
                  description={WRITEBACK_PROPOSALS_FILTERED_EMPTY_NOTE}
                />
              )
            ) : (
              proposals.map(({ node }) => (
                <div
                  key={node.id}
                  data-testid={`writeback-proposal-${node.id}`}
                  style={{
                    borderTop: "1px solid var(--vd-border)",
                    paddingTop: 12,
                  }}
                >
                  <div
                    className="vd-row"
                    style={{ justifyContent: "space-between" }}
                  >
                    <div className="vd-card-title">
                      {WRITEBACK_TARGET_LABEL[node.target]}
                      {node.productId === null ? null : `（${node.productId}）`}
                      <Tag tone={STATE_TONE[node.state]}>
                        {WRITEBACK_PROPOSAL_STATE_LABEL[node.state]}
                      </Tag>
                    </div>
                    {node.state === "PENDING" ? (
                      <div className="vd-row">
                        <Button
                          variant="primary"
                          disabled={approveResult.fetching}
                          onClick={() => {
                            void decide(node.id, "APPROVE");
                          }}
                        >
                          承認する
                        </Button>
                        <Button
                          disabled={rejectResult.fetching}
                          onClick={() => {
                            void decide(node.id, "REJECT");
                          }}
                        >
                          却下する
                        </Button>
                      </div>
                    ) : null}
                  </div>
                  <p
                    style={{
                      margin: "4px 0 0",
                      color: "var(--vd-text-secondary)",
                    }}
                  >
                    {node.reason}
                  </p>
                  <p className="vd-stat-note" style={{ margin: "4px 0 0" }}>
                    提案 {formatJstDateTime(node.proposedAt)}
                    {node.approvedAt === null
                      ? ""
                      : ` ・ 承認 ${formatJstDateTime(node.approvedAt)}`}
                    {node.approvedBy === null
                      ? ""
                      : `（${node.approvedBy.displayName}）`}
                    {` ・ 許可の範囲 ${WRITEBACK_MODE_LABEL[node.mode]}`}
                    {/*
                      受付番号（ULID の先頭）は出さない。人がこの番号で問い合わせる
                      口がどこにも無く、行は「対象・商品名・起こした日時」で十分に
                      見分けられる。内部識別子は行の data-testid にだけ残す。
                    */}
                  </p>
                  {node.mode === "AUTOMATIC" ? (
                    <p className="vd-stat-note" style={{ margin: "4px 0 0" }}>
                      {WRITEBACK_AUTOMATIC_NOT_CONNECTED_NOTE}
                    </p>
                  ) : null}
                  <p className="vd-stat-note" style={{ margin: "4px 0 0" }}>
                    反映した時刻: {NOT_MEASURED}（EC
                    側の口がつながっていないため、まだ反映していません）
                  </p>
                  {SUPPORTS_WRITEBACK_REVERT ? null : (
                    <p className="vd-stat-note" style={{ margin: "4px 0 0" }}>
                      {WRITEBACK_NO_PREVIOUS_VALUE_NOTE}
                    </p>
                  )}
                  <Disclosure summary="反映しようとしている値">
                    <pre className="vd-code">{node.proposedValue}</pre>
                  </Disclosure>
                </div>
              ))
            )}
          </div>
        </Card>
      </div>
    </div>
  );
}
